// Premium Propulse Scratch Card — foil-shimmer scratch surface with diagonal sparkle,
// reveal explosion, prize-tier confetti, fireworks for jackpot prizes, learning insight after reveal.
import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, Volume2, VolumeX, Sparkles, Award, Lightbulb, Trophy } from 'lucide-react';
import { GameFrame, AnnouncementBanner, ScoreTile, playGameSound, GoldButton } from './shared/GameFX';
import { ConfettiBurst, Fireworks } from './ludo/LudoConfetti';

function pickPrize(prizes) {
    const total = prizes.reduce((s, p) => s + p.weight, 0);
    let r = Math.random() * total;
    for (const p of prizes) {
        if (r < p.weight) return p;
        r -= p.weight;
    }
    return prizes[0];
}

const PRIZE_TIPS = [
    { title: 'FR vs FRLS', body: 'FRLS adds low-smoke generation to flame retardance — vital for indoor crowded spaces.' },
    { title: 'IS 694 in 30s', body: 'The Indian Standard for PVC insulated single-core house wires up to 1100V.' },
    { title: 'XLPE Advantage', body: 'XLPE handles 90°C continuous operation vs PVC at 70°C — better for heavy loads.' },
    { title: 'Counterfeit Test', body: 'Pure copper is non-magnetic. A magnetic pull on a cable conductor means CCA.' },
    { title: 'Drum Storage', body: 'Store cable drums upright on flanges in dry, covered storage.' },
];

export default function ScratchCard({ game, onPlayed }) {
    const canvasRef = useRef(null);
    const [prize, setPrize] = useState(() => pickPrize(game.config?.prizes || [{ label: '+10 pts', value: 10 }]));
    const [revealed, setRevealed] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [muted, setMuted] = useState(false);
    const [announce, setAnnounce] = useState(null);
    const [bursts, setBursts] = useState([]);
    const [showFireworks, setShowFireworks] = useState(false);
    const [revealCount, setRevealCount] = useState(0);
    const [totalWon, setTotalWon] = useState(0);
    const [tip, setTip] = useState(null);
    const burstIdRef = useRef(0);

    const triggerBurst = (kind = 'normal') => {
        burstIdRef.current += 1;
        const id = burstIdRef.current;
        const cfg = kind === 'jackpot' ? { count: 60, scale: 1.4 } : { count: 30, scale: 1.0 };
        setBursts((b) => [...b, { id, ...cfg }]);
        setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), 1700);
    };

    const announce_ = (text, tone = 'gold') => {
        const id = Date.now() + Math.random();
        setAnnounce({ text, tone, id });
        setTimeout(() => setAnnounce((a) => (a && a.id === id ? null : a)), 1500);
    };

    const drawCover = () => {
        const cvs = canvasRef.current;
        if (!cvs) return;
        const ctx = cvs.getContext('2d');
        ctx.globalCompositeOperation = 'source-over';
        // Metallic diagonal foil gradient
        const grad = ctx.createLinearGradient(0, 0, cvs.width, cvs.height);
        grad.addColorStop(0, '#B0B7C2');
        grad.addColorStop(0.3, '#8C95A3');
        grad.addColorStop(0.5, '#D9DDE5');
        grad.addColorStop(0.7, '#8C95A3');
        grad.addColorStop(1, '#5D6675');
        ctx.fillStyle = grad; ctx.fillRect(0, 0, cvs.width, cvs.height);
        // Holographic shimmer dots
        for (let i = 0; i < 80; i++) {
            const x = Math.random() * cvs.width;
            const y = Math.random() * cvs.height;
            ctx.fillStyle = `rgba(255,255,255,${0.15 + Math.random() * 0.25})`;
            ctx.beginPath(); ctx.arc(x, y, 1.5 + Math.random() * 2, 0, Math.PI * 2); ctx.fill();
        }
        // Diagonal sparkles
        ctx.fillStyle = 'rgba(255,255,255,0.08)';
        for (let i = -cvs.height; i < cvs.width; i += 18) {
            ctx.fillRect(i, 0, 3, cvs.height);
        }
        // Center text
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.font = 'bold 28px Manrope, system-ui';
        ctx.textAlign = 'center';
        ctx.fillText('SCRATCH HERE', cvs.width / 2, cvs.height / 2 - 6);
        ctx.font = 'bold 13px Manrope, system-ui';
        ctx.fillStyle = 'rgba(255,255,255,0.65)';
        ctx.fillText('drag your finger to reveal the reward', cvs.width / 2, cvs.height / 2 + 22);
        // Small UT logo top-left
        ctx.font = 'bold 11px Manrope, system-ui';
        ctx.textAlign = 'left';
        ctx.fillStyle = 'rgba(255,255,255,0.5)';
        ctx.fillText('Propulse · Daily Scratch', 14, 22);
        ctx.globalCompositeOperation = 'destination-out';
    };

    useEffect(() => { drawCover(); /* eslint-disable-next-line */ }, [prize.label]);

    const reveal = async () => {
        if (revealed || submitted) return;
        setRevealed(true); setSubmitted(true);
        const cvs = canvasRef.current;
        if (cvs) {
            const ctx = cvs.getContext('2d');
            ctx.globalCompositeOperation = 'source-over';
            ctx.clearRect(0, 0, cvs.width, cvs.height);
        }
        setRevealCount((c) => c + 1);
        setTotalWon((t) => t + (prize.value || 0));
        const isJackpot = (prize.value || 0) >= 50;
        if (isJackpot) {
            playGameSound('jackpot', muted);
            setShowFireworks(true);
            setTimeout(() => setShowFireworks(false), 3500);
            triggerBurst('jackpot');
            announce_(`🏆 JACKPOT! ${prize.label}`, 'gold');
        } else {
            playGameSound('correct', muted);
            triggerBurst('normal');
            announce_(`🎁 ${prize.label}`, 'green');
        }
        setTip(PRIZE_TIPS[Math.floor(Math.random() * PRIZE_TIPS.length)]);
        try {
            await api.post(`/games/${game.code}/submit`, { score: prize.value, awarded_points: prize.value, details: { prize: prize.label } });
            onPlayed && onPlayed();
        } catch (e) { console.warn('Scratch submit failed', e); }
    };

    const playAgain = () => {
        setPrize(pickPrize(game.config?.prizes || [{ label: '+10 pts', value: 10 }]));
        setRevealed(false); setSubmitted(false); setTip(null); setShowFireworks(false);
        setTimeout(drawCover, 50);
    };

    const scratchAt = (clientX, clientY) => {
        const cvs = canvasRef.current; if (!cvs || revealed) return;
        const rect = cvs.getBoundingClientRect();
        const x = (clientX - rect.left) * (cvs.width / rect.width);
        const y = (clientY - rect.top) * (cvs.height / rect.height);
        const ctx = cvs.getContext('2d');
        ctx.beginPath();
        ctx.arc(x, y, 36, 0, Math.PI * 2);
        ctx.fill();
        if (Math.random() < 0.06) {
            const data = ctx.getImageData(0, 0, cvs.width, cvs.height).data;
            let cleared = 0, total = 0;
            for (let i = 3; i < data.length; i += 4 * 80) { total++; if (data[i] === 0) cleared++; }
            const pct = cleared / total;
            if (pct > 0.45) reveal();
        }
    };

    const isJackpot = (prize.value || 0) >= 50;

    return (
        <GameFrame dataTestId="scratch-card-game" orbs={[
            { color: '#FFF200', x: '12%', y: '20%', size: 230, delay: 0 },
            { color: '#E31E24', x: '85%', y: '25%', size: 210, delay: 1.4 },
            { color: '#a855f7', x: '50%', y: '90%', size: 240, delay: 2.8 },
        ]}>
            <Header muted={muted} onToggleMute={() => setMuted((m) => !m)} revealCount={revealCount} totalWon={totalWon} />
            <div className="mt-5 flex flex-col items-center">
                {/* Card frame */}
                <div className="relative">
                    {/* Outer glow */}
                    <motion.div
                        animate={{ opacity: revealed && isJackpot ? [0.5, 1, 0.5] : 0.4 }}
                        transition={revealed && isJackpot ? { repeat: Infinity, duration: 1.5 } : { duration: 0 }}
                        className="absolute -inset-3 rounded-3xl pointer-events-none"
                        style={{ background: 'radial-gradient(circle, rgba(255,242,0,0.35) 0%, transparent 70%)', filter: 'blur(20px)' }} />

                    <div className="relative w-[340px] sm:w-[400px] aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl select-none ring-4 ring-amber-300/50"
                        style={{
                            background: isJackpot
                                ? 'linear-gradient(135deg, #FFD700 0%, #F59E0B 35%, #DC2626 75%, #7C2D12 100%)'
                                : 'linear-gradient(135deg, #F47E1F 0%, #E31E24 100%)',
                        }}>
                        {/* Diagonal shimmer when not revealed */}
                        {!revealed && (
                            <motion.div
                                animate={{ backgroundPosition: ['-200% 0%', '200% 0%'] }}
                                transition={{ repeat: Infinity, duration: 3.5, ease: 'linear' }}
                                className="absolute inset-0 pointer-events-none opacity-50"
                                style={{ background: 'linear-gradient(90deg, transparent 30%, rgba(255,255,255,0.4) 50%, transparent 70%)', backgroundSize: '200% 100%' }} />
                        )}
                        {/* Prize face */}
                        <motion.div
                            animate={{ scale: revealed ? [0.85, 1.15, 1] : 0.95 }}
                            transition={revealed ? { duration: 0.7 } : { duration: 0 }}
                            className="absolute inset-0 flex flex-col items-center justify-center text-white p-6">
                            <div className="text-[10px] uppercase tracking-[0.3em] mb-3 opacity-80 inline-flex items-center gap-1">
                                <Sparkles className="h-3 w-3" /> {isJackpot ? 'JACKPOT' : 'You won'}
                            </div>
                            <div className="font-display font-extrabold text-5xl sm:text-6xl drop-shadow-2xl" data-testid="scratch-prize">{prize.label}</div>
                            {isJackpot && revealed && (
                                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                                    className="mt-3 inline-flex items-center gap-1.5 text-yellow-200 font-display font-extrabold text-sm">
                                    <Trophy className="h-4 w-4" /> RARE WIN
                                </motion.div>
                            )}
                        </motion.div>
                        {/* Frame border */}
                        <div className="absolute inset-3 rounded-2xl border-2 border-white/30 pointer-events-none" />
                        {/* Scratch overlay */}
                        {!revealed && (
                            <canvas ref={canvasRef} width={500} height={375}
                                className="absolute inset-0 w-full h-full cursor-crosshair touch-none"
                                onMouseDown={(e) => { setDragging(true); scratchAt(e.clientX, e.clientY); }}
                                onMouseUp={() => setDragging(false)}
                                onMouseLeave={() => setDragging(false)}
                                onMouseMove={(e) => dragging && scratchAt(e.clientX, e.clientY)}
                                onTouchStart={(e) => { setDragging(true); const t = e.touches[0]; if (t) scratchAt(t.clientX, t.clientY); }}
                                onTouchEnd={() => setDragging(false)}
                                onTouchMove={(e) => { e.preventDefault(); const t = e.touches[0]; if (t) scratchAt(t.clientX, t.clientY); }}
                                data-testid="scratch-canvas"
                            />
                        )}
                    </div>
                </div>

                {/* Action buttons */}
                <div className="mt-5 flex items-center justify-center gap-3">
                    {!revealed && (
                        <GoldButton onClick={reveal} dataTestId="scratch-reveal-btn">
                            <Award className="h-4 w-4" /> Reveal Now
                        </GoldButton>
                    )}
                    {revealed && (
                        <GoldButton onClick={playAgain} dataTestId="scratch-play-again-btn">
                            <RefreshCw className="h-4 w-4" /> Play Again
                        </GoldButton>
                    )}
                </div>

                {/* Learning tip after reveal */}
                <AnimatePresence>
                    {tip && (
                        <motion.div
                            key={tip.title}
                            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -10, opacity: 0 }}
                            className="mt-6 w-full max-w-md p-4 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 shadow-lg"
                            data-testid="scratch-tip">
                            <div className="flex items-center gap-2 text-emerald-700 text-[10px] uppercase tracking-wider font-bold">
                                <Lightbulb className="h-3 w-3" /> Learning Tip
                            </div>
                            <div className="font-display font-extrabold text-slate-900 mt-1">{tip.title}</div>
                            <div className="text-sm text-slate-700 mt-1 leading-relaxed">{tip.body}</div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {bursts.map((b) => <ConfettiBurst key={b.id} count={b.count} scale={b.scale} />)}
            {showFireworks && <Fireworks duration={3.5} density={1.4} />}
            <AnnouncementBanner announce={announce} />
        </GameFrame>
    );
}

function Header({ muted, onToggleMute, revealCount, totalWon }) {
    return (
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-red-700 flex items-center justify-center"
                style={{ boxShadow: '0 0 20px rgba(244,63,94,0.5)' }}>
                <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-amber-300">Propulse Cables & Wires</div>
                <div className="font-display font-extrabold text-lg sm:text-2xl text-white truncate">Daily Scratch Card</div>
                <div className="text-[11px] sm:text-xs text-slate-400 truncate">Scratch the foil · uncover a reward · learn a tip</div>
            </div>
            <div className="flex gap-2">
                <ScoreTile label="Cards" value={revealCount} accent="from-sky-400 to-blue-500" />
                <ScoreTile label="Pts" value={totalWon} accent="from-amber-400 to-yellow-500" />
            </div>
            <button onClick={onToggleMute} className="w-9 h-9 rounded-xl bg-white/10 text-white flex items-center justify-center hover:bg-white/20" data-testid="scratch-mute-btn">
                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
        </div>
    );
}
